import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { axiosInstance } from "@/lib/axios-instance";
import { getErrorMessage } from "@/lib/api-error";
import { useUser, useSignIn, useSignUp } from "@clerk/clerk-react";
import { Loader2, Mail, ArrowLeft } from "lucide-react";
import React, { useEffect, useState } from "react";

interface BasicInfoStepProps {
  formData: any;
  setFormData: (data: any) => void;
  nextStep: () => void;
  setCompanyInfo: (company: any) => void;
}

type AuthStep = "form" | "otp";

export default function BasicInfoStep({
  formData,
  setFormData,
  nextStep,
  setCompanyInfo,
}: BasicInfoStepProps) {
  const { user } = useUser();
  const { isLoaded: isSignInLoaded, signIn, setActive } = useSignIn();
  const {
    isLoaded: isSignUpLoaded,
    signUp,
    setActive: setSignUpActive,
  } = useSignUp();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showLogin, setShowLogin] = useState(false);
  const [loginData, setLoginData] = useState({ email: "" });
  const [loginErrors, setLoginErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState("");

  const [authStep, setAuthStep] = useState<AuthStep>("form");
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState("");

  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  useEffect(() => {
    if (formData.foundedMonth) {
      setSelectedMonth(formData.foundedMonth);
    }
    if (formData.foundedYear) {
      setSelectedYear(formData.foundedYear);
    }
  }, [formData]);

  const fieldLabels = {
    firstName: "First Name",
    lastName: "Last Name",
    email: "Email",
    companyName: "Company Name",
  };

  const isFormValid = () => {
    let valid = true;
    const errorsTemp: Record<string, string> = {};

    if (user) {
      if (!formData.companyName) {
        errorsTemp.companyName = "Company Name is required";
        valid = false;
      }
    } else {
      if (!formData.firstName) {
        errorsTemp.firstName = "First Name is required";
        valid = false;
      }

      if (!formData.lastName) {
        errorsTemp.lastName = "Last Name is required";
        valid = false;
      }

      if (!formData.email) {
        errorsTemp.email = "Email is required";
        valid = false;
      } else {
        const emailValid = formData.email.match(
          /^([\w.%+-]+)@([\w-]+\.)+(\w{2,})$/i
        );
        if (!emailValid) {
          errorsTemp.email = "Email is invalid";
          valid = false;
        }
      }

      if (!formData.companyName) {
        errorsTemp.companyName = "Company Name is required";
        valid = false;
      }
    }

    setErrors(errorsTemp);
    return valid;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const trimmedValue = name === "companyName" ? value : value.trim();
    setFormData({ ...formData, [name]: trimmedValue });
    handleUserInput(name, trimmedValue);
  };

  const handleUserInput = (name: string, value: string) => {
    const errorsTemp = { ...errors };
    const fieldLabel = fieldLabels[name as keyof typeof fieldLabels] || name;

    if (!value) {
      errorsTemp[name] = `${fieldLabel} is required`;
    } else {
      if (name === "email") {
        const emailValid = value.match(/^([\w.%+-]+)@([\w-]+\.)+(\w{2,})$/i);
        errorsTemp.email = emailValid ? "" : "Email is invalid";
      } else if (name === "companyName") {
        errorsTemp.companyName =
          value.length < 2 ? "Company Name must be at least 2 characters" : "";
      } else {
        errorsTemp[name] = "";
      }
    }

    setErrors(errorsTemp);
  };

  const handleSendSignupOTP = async () => {
    if (Object.values(errors).some((error) => error) || !isFormValid()) {
      return;
    }
    setIsSubmitting(true);
    setApiError("");

    try {
      if (!isSignUpLoaded || !signUp) {
        throw new Error("Authentication service is still initializing.");
      }

      await signUp.create({
        emailAddress: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
      });

      await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
      setAuthStep("otp");
    } catch (error: any) {
      console.error("Error sending OTP:", error);
      const errMsg =
        error?.errors?.[0]?.longMessage ||
        error?.message ||
        "Failed to send OTP";
      setApiError(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifySignupOTP = async () => {
    if (!otpCode || otpCode.length < 6) {
      setOtpError("Please enter the 6-digit code");
      return;
    }
    setIsSubmitting(true);
    setOtpError("");
    setApiError("");

    try {
      if (!signUp) {
        throw new Error("Sign up session not found.");
      }
      const completeSignUp = await signUp.attemptEmailAddressVerification({
        code: otpCode,
      });

      if (completeSignUp.status === "complete") {
        await setSignUpActive({ session: completeSignUp.createdSessionId });

        const companyResponse = await axiosInstance.post("/create_company", {
          company_name: String(formData.companyName) || "",
          company_type: ["startup"],
          founded_year: selectedYear,
        });

        if (companyResponse.data.status === "Already Submitted") {
          setApiError("You already have a company with this name.");
          return;
        }

        if (!companyResponse.data.result || !companyResponse.data.result._id) {
          throw new Error(
            companyResponse.data.message || "Failed to create company"
          );
        }

        const companyData = companyResponse.data.result;
        setCompanyInfo(companyData);
        nextStep();
      } else {
        throw new Error("Verification incomplete. Please try again.");
      }
    } catch (error: any) {
      console.error("Error verifying OTP:", error);
      const errMsg =
        error?.errors?.[0]?.longMessage || error?.message || "Invalid code";
      setOtpError(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const trimmedValue = value.trim();
    setLoginData({ ...loginData, [name]: trimmedValue });
    handleLoginInput(name, trimmedValue);
  };

  const handleLoginInput = (name: string, value: string) => {
    const errorsTemp = { ...loginErrors };
    const fieldLabel = fieldLabels[name as keyof typeof fieldLabels] || name;

    if (!value) {
      errorsTemp[name] = `${fieldLabel} is required`;
    } else {
      if (name === "email") {
        const emailValid = value.match(/^([\w.%+-]+)@([\w-]+\.)+(\w{2,})$/i);
        errorsTemp.email = emailValid ? "" : "Email is invalid";
      } else {
        errorsTemp[name] = "";
      }
    }

    setLoginErrors(errorsTemp);
  };

  const handleSendLoginOTP = async () => {
    if (Object.values(loginErrors).some((error) => error) || !loginData.email) {
      if (!loginData.email) {
        setLoginErrors({ email: "Email is required" });
      }
      return;
    }
    setIsSubmitting(true);
    setApiError("");

    try {
      if (!isSignInLoaded || !signIn) {
        throw new Error("Authentication service is still initializing.");
      }

      const signInAttempt = await signIn.create({
        identifier: loginData.email,
      });

      const emailFactor = signInAttempt.supportedFirstFactors?.find(
        (factor) => factor.strategy === "email_code"
      ) as { strategy: "email_code"; emailAddressId: string } | undefined;

      if (!emailFactor) {
        throw new Error(
          "Email verification is not available for this account."
        );
      }

      await signIn.prepareFirstFactor({
        strategy: "email_code",
        emailAddressId: emailFactor.emailAddressId,
      });

      setAuthStep("otp");
    } catch (error: any) {
      console.error("Error sending login OTP:", error);
      const errMsg =
        error?.errors?.[0]?.longMessage ||
        error?.message ||
        "Failed to send OTP";
      setApiError(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyLoginOTP = async () => {
    if (!otpCode || otpCode.length < 6) {
      setOtpError("Please enter the 6-digit code");
      return;
    }
    setIsSubmitting(true);
    setOtpError("");
    setApiError("");

    try {
      if (!signIn) {
        throw new Error("Sign in session not found.");
      }
      const completeSignIn = await signIn.attemptFirstFactor({
        strategy: "email_code",
        code: otpCode,
      });

      if (completeSignIn.status === "complete") {
        await setActive({ session: completeSignIn.createdSessionId });
        nextStep();
      } else {
        throw new Error("Verification incomplete. Please try again.");
      }
    } catch (error: any) {
      console.error("Error verifying login OTP:", error);
      const errMsg =
        error?.errors?.[0]?.longMessage || error?.message || "Invalid code";
      setOtpError(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOTP = async () => {
    setIsSubmitting(true);
    setOtpError("");
    try {
      if (showLogin) {
        const emailFactor = signIn?.supportedFirstFactors?.find(
          (factor) => factor.strategy === "email_code"
        ) as { strategy: "email_code"; emailAddressId: string } | undefined;
        if (emailFactor) {
          await signIn?.prepareFirstFactor({
            strategy: "email_code",
            emailAddressId: emailFactor.emailAddressId,
          });
        }
      } else {
        await signUp?.prepareEmailAddressVerification({
          strategy: "email_code",
        });
      }
      setApiError("");
    } catch (error: any) {
      console.error("Error resending OTP:", error);
      setOtpError("Failed to resend code. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackFromOTP = () => {
    setAuthStep("form");
    setOtpCode("");
    setOtpError("");
    setApiError("");
  };

  const handleNextButtonClick = async () => {
    if (user) {
      if (!formData.companyName) {
        setErrors({ companyName: "Company Name is required" });
        return;
      }
      setIsSubmitting(true);
      try {
        const companyResponse = await axiosInstance.post("/create_company", {
          company_name: String(formData.companyName) || "",
          company_type: ["startup"],
          founded_year: selectedYear,
        });

        if (companyResponse.data.status === "Already Submitted") {
          setApiError("You already have a company with this name.");
          return;
        }

        if (!companyResponse.data.result || !companyResponse.data.result._id) {
          throw new Error(
            companyResponse.data.message || "Failed to create company"
          );
        }

        const companyData = companyResponse.data.result;
        setCompanyInfo(companyData);
        nextStep();
      } catch (error: any) {
        setApiError(getErrorMessage(error, "Failed to create company"));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      await handleSendSignupOTP();
    }
  };

  const monthOptions = Array.from({ length: 12 }, (_, i) => i + 1);
  const yearOptions = Array.from(
    { length: 100 },
    (_, i) => currentYear - i
  ).reverse();

  if (authStep === "otp") {
    return (
      <div className="max-w-2xl mx-auto p-4 md:p-6">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackFromOTP}
                className="p-0 h-auto"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <CardTitle>Verify Your Email</CardTitle>
            </div>
            <p className="text-gray-600">
              We sent a 6-digit code to{" "}
              <span className="font-medium">
                {showLogin ? loginData.email : formData.email}
              </span>
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {apiError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{apiError}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="otpCode">Verification Code</Label>
              <Input
                id="otpCode"
                type="text"
                inputMode="numeric"
                placeholder="Enter 6-digit code"
                value={otpCode}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setOtpCode(val);
                  setOtpError("");
                }}
                maxLength={6}
                className="text-center text-2xl tracking-widest"
              />
              {otpError && (
                <p className="text-sm text-red-600 mt-1">{otpError}</p>
              )}
            </div>

            <Button
              onClick={showLogin ? handleVerifyLoginOTP : handleVerifySignupOTP}
              disabled={isSubmitting || otpCode.length < 6}
              className="w-full"
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Verifying...
                </div>
              ) : (
                "Verify & Continue"
              )}
            </Button>

            <div className="text-center">
              <p className="text-sm text-gray-600">
                Didn't receive the code?{" "}
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={isSubmitting}
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  Resend
                </button>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4 md:p-6">
      <Card>
        <CardHeader>
          <CardTitle>Basic Info</CardTitle>
          <p className="text-gray-600">
            Provide Info about yourself before we proceed.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {apiError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{apiError}</p>
            </div>
          )}

          {!showLogin ? (
            <div className="space-y-4">
              {!user ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName">First Name:</Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        placeholder="Enter your First name"
                        value={formData.firstName || ""}
                        onChange={handleInputChange}
                      />
                      {errors.firstName && (
                        <p className="text-sm text-red-600 mt-1">
                          {errors.firstName}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="lastName">Last Name:</Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        placeholder="Enter your Last name"
                        value={formData.lastName || ""}
                        onChange={handleInputChange}
                      />
                      {errors.lastName && (
                        <p className="text-sm text-red-600 mt-1">
                          {errors.lastName}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email">Email:</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="Enter your email"
                      value={formData.email || ""}
                      onChange={handleInputChange}
                    />
                    {errors.email && (
                      <p className="text-sm text-red-600 mt-1">
                        {errors.email}
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center mb-4">
                  <p className="text-lg font-medium text-gray-900">
                    Welcome back, {user.firstName}!
                  </p>
                  <p className="text-sm text-gray-600">
                    Let's create a company for your application.
                  </p>
                </div>
              )}

              <div>
                <Label htmlFor="companyName">Company Name:</Label>
                <Input
                  id="companyName"
                  name="companyName"
                  placeholder="Enter the name of your company"
                  value={formData.companyName || ""}
                  onChange={handleInputChange}
                />
                {errors.companyName && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.companyName}
                  </p>
                )}
              </div>

              <div>
                <Label>Founded On:</Label>
                <div className="grid grid-cols-2 gap-4">
                  <select
                    name="foundedMonth"
                    value={selectedMonth}
                    onChange={(e) => {
                      setSelectedMonth(parseInt(e.target.value));
                      setFormData({
                        ...formData,
                        foundedMonth: parseInt(e.target.value),
                      });
                    }}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    {monthOptions.map((month) => (
                      <option key={month} value={month}>
                        {month.toString().padStart(2, "0")}
                      </option>
                    ))}
                  </select>
                  <select
                    name="foundedYear"
                    value={selectedYear}
                    onChange={(e) => {
                      setSelectedYear(parseInt(e.target.value));
                      setFormData({
                        ...formData,
                        foundedYear: parseInt(e.target.value),
                      });
                    }}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    {yearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {!user && (
                <div className="text-center">
                  <p
                    onClick={() => setShowLogin(true)}
                    className="cursor-pointer text-blue-600 hover:text-blue-800 underline text-sm"
                  >
                    Already have an account? Login
                  </p>
                </div>
              )}

              <div className="pt-2">
                <Button
                  onClick={handleNextButtonClick}
                  disabled={isSubmitting}
                  className="w-full"
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {user ? "Creating Company..." : "Sending OTP..."}
                    </div>
                  ) : user ? (
                    "Create Company"
                  ) : (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Send OTP & Continue
                    </div>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label htmlFor="loginEmail">Email:</Label>
                <Input
                  id="loginEmail"
                  name="email"
                  type="email"
                  placeholder="Enter your email"
                  value={loginData.email}
                  onChange={handleLoginChange}
                />
                {loginErrors.email && (
                  <p className="text-sm text-red-600 mt-1">
                    {loginErrors.email}
                  </p>
                )}
              </div>

              <div className="text-center">
                <p
                  onClick={() => setShowLogin(false)}
                  className="cursor-pointer text-blue-600 hover:text-blue-800 underline text-sm"
                >
                  Don't have an account? Sign up
                </p>
              </div>

              <div className="pt-2">
                <Button
                  onClick={handleSendLoginOTP}
                  disabled={isSubmitting}
                  className="w-full"
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending OTP...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Send OTP & Login
                    </div>
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
